import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import ApartmentForm, { type ApartmentRecord } from '@/pages/dashboard/apartments/components/ApartmentForm';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

function ApartmentEdit() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [existing, setExisting] = useState<ApartmentRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!user || !id) return;
      setLoading(true);
      try {
        const { data: biz } = await supabase
          .from('businesses')
          .select('id')
          .eq('owner_id', user.id)
          .maybeSingle();
        if (!mounted) return;
        setBusinessId(biz?.id ?? null);

        const { data: apt } = await supabase
          .from('apartments')
          .select('*')
          .eq('id', id)
          .eq('owner_id', user.id)
          .maybeSingle();

        if (!mounted) return;
        if (!apt) {
          setNotFound(true);
        } else {
          const record = apt as ApartmentRecord;
          setExisting({
            id: record.id,
            business_id: record.business_id,
            owner_id: record.owner_id,
            title: record.title || '',
            type: record.type || 'Appartement',
            transaction: record.transaction || 'location',
            city: record.city || 'Lomé',
            quartier: record.quartier || '',
            price: Number(record.price) || 0,
            bedrooms: Number(record.bedrooms) || 0,
            bathrooms: Number(record.bathrooms) || 0,
            surface: Number(record.surface) || 0,
            description: record.description || '',
            images: Array.isArray(record.images) ? record.images : [],
            amenities: Array.isArray(record.amenities) ? record.amenities : [],
            available: Boolean(record.available),
            owner_type: record.owner_type || 'proprietaire',
            owner_name: record.owner_name || '',
            owner_phone: record.owner_phone || '',
            reservation: record.reservation || { checkin: '14:00', checkout: '12:00', bookable: true },
          });
        }
      } catch {
        if (mounted) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [user, id]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
            <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (notFound || !existing) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <h1 className="font-heading font-bold text-2xl text-foreground-50 mb-2">Bien introuvable</h1>
          <p className="text-sm text-foreground-400 mb-6">Ce bien n&apos;existe pas ou ne vous appartient pas.</p>
          <button
            onClick={() => navigate('/dashboard/apartments')}
            className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
          >
            Retour à mes appartements
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">Modifier le bien</h1>
        <p className="text-sm text-foreground-400 mt-1">Mettez à jour les informations de votre bien.</p>
      </div>

      <div className="max-w-3xl">
        <ApartmentForm
          businessId={businessId}
          ownerId={user!.id}
          existing={existing}
          onSaved={() => navigate('/dashboard/apartments')}
        />
      </div>
    </DashboardLayout>
  );
}

export default ApartmentEdit;